import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { first } from 'rxjs/operators';
import { AnnexService } from 'src/app/data/annex.service';
import { SignaturePolicyDto } from 'src/app/models/dtos/annexes/signature-policy-dto';
import { UserPermissionsDto } from 'src/app/models/dtos/annexes/user-permissions-dto';
import { CurrentSignatureDto } from 'src/app/models/dtos/annexes/current-signature-dto';

@Component({
  selector: 'successfully-created-annex',
  templateUrl: './successfully-created-annex.component.html',
  styleUrls: ['./successfully-created-annex.component.scss'],
  standalone: false
})
export class SuccessfullyCreatedAnnexComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private annexService = inject(AnnexService);

  annexId: number;
  workerId: number;
  employmentTypeId: number;
  agreementId: number;
  canSignAsFirst = false;

  ngOnInit(): void {
    const params = this.route.snapshot.params;
    const queryParams = this.route.snapshot.queryParams;

    this.annexId = +params['annexId'];
    this.workerId = +queryParams['workerId'];
    this.employmentTypeId = +queryParams['employmentTypeId'];
    this.agreementId = +queryParams['agreementId'];

    if (this.annexId) {
      this.loadSignatureContext();
    }
  }

  get agreementDetailsLink(): (string | number)[] {
    return ['/workers', this.workerId, 'employmentType', this.employmentTypeId, 'agreements', this.agreementId, 'details'];
  }

  get signAnnexLink(): (string | number)[] {
    return ['/workers', this.workerId, 'employmentType', this.employmentTypeId, 'agreements', this.agreementId, 'annexes', this.annexId, 'signature'];
  }

  private loadSignatureContext(): void {
    this.annexService
      .getAnnexForSignature(this.annexId)
      .pipe(first())
      .subscribe({
        next: (data) => {
          const policies: SignaturePolicyDto[] = data?.SignaturePolicies || [];
          const userPermissions: UserPermissionsDto = data?.UserPermissions;
          const currentSignatures: CurrentSignatureDto[] = data?.CurrentSignatures || [];

          this.canSignAsFirst = this.evaluateCanSignAsFirst(policies, userPermissions, currentSignatures);
        },
        error: () => {
          this.canSignAsFirst = false;
        },
      });
  }

  private evaluateCanSignAsFirst(
    policies: SignaturePolicyDto[],
    userPermissions: UserPermissionsDto,
    currentSignatures: CurrentSignatureDto[],
  ): boolean {
    if (!userPermissions?.CanSign || userPermissions.SignatureTypeId == null || policies.length === 0) {
      return false;
    }

    const firstPolicy = policies.reduce((min, p) => (p.Order < min.Order ? p : min), policies[0]);

    if (firstPolicy.SignatureTypeId !== userPermissions.SignatureTypeId) {
      return false;
    }

    const alreadySignedForStep = currentSignatures.filter(
      (s) => s.SignatureTypeId === firstPolicy.SignatureTypeId,
    ).length;

    return alreadySignedForStep < firstPolicy.RequiredQuantity;
  }
}